import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";

interface NavbarProps {
  onOpenConsultation?: () => void;
  variant?: "light" | "dark";
}

export function Navbar({ onOpenConsultation }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [location] = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleConsultation = () => {
    if (onOpenConsultation) {
      onOpenConsultation();
    } else {
      trackEvent("consultation_click", { source: "navbar" });
      window.open("https://calendar.app.google/q7DbU8wvN6oR8h918", "_blank", "noopener,noreferrer");
    }
  };

  const handleNavLinkClick = (linkName: string) => {
    setMobileMenuOpen(false);

    const sectionId =
      linkName === "Portfolio"
        ? "portfolio"
        : linkName === "Reviews"
          ? "reviews"
          : null;

    if (sectionId) {
      window.setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 0);
    }
  };

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "Programmes", href: "/programmes" },
    { name: "Portfolio", href: "/#portfolio" },
    { name: "About", href: "/about" },
    { name: "Contact", href: "/contact" },
    { name: "Reviews", href: "/#reviews" },
  ];
  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/80 backdrop-blur-lg border-b border-white/10 shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 group cursor-pointer">
            <div className="w-10 h-10 rounded-full overflow-hidden shadow-lg shadow-primary/20 group-hover:shadow-primary/50 transition-all group-hover:scale-105">
              <img
                src={`${import.meta.env.BASE_URL}images/fisl-mark-288.webp`}
                alt="FISL Logo"
                width={40}
                height={40}
                decoding="async"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex flex-col">
              <span className="font-display font-bold text-xl leading-tight tracking-tight text-white group-hover:text-glow transition-all">
                FISL
              </span>
              <span className="text-[10px] text-muted-foreground uppercase tracking-widest hidden sm:block">
                Future Intelligence Systems Ltd
              </span>
              <span className="text-[8px] text-muted-foreground uppercase tracking-widest hidden sm:block">
                A United Kingdom Registered Company
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <div className="flex gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  href={link.href}
                  onClick={() => handleNavLinkClick(link.name)}
                  className={`text-sm font-medium transition-colors ${
                    location === link.href
                      ? "text-primary font-semibold"
                      : "text-muted-foreground hover:text-white"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
            <Button 
              onClick={handleConsultation}
              className="bg-primary/10 text-primary border border-primary/50 hover:bg-primary hover:text-black hover:font-bold hover:shadow-[0_0_20px_rgba(212,160,23,0.5)] transition-all duration-300 rounded-full px-6"
              data-testid="button-nav-consultation"
            >
              Book Your Free Consultation
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-muted-foreground hover:text-white p-2"
              aria-label="Toggle menu"
              data-testid="button-mobile-menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background/95 backdrop-blur-xl border-b border-white/10 absolute w-full">
          <div className="px-4 pt-2 pb-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                href={link.href}
                onClick={() => handleNavLinkClick(link.name)}
                className={`block px-3 py-3 text-base font-medium rounded-md ${
                  location === link.href
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-white hover:bg-white/5"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 px-3">
              <Button 
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleConsultation();
                }}
                className="w-full bg-primary hover:bg-primary/90 text-black font-bold rounded-xl"
                data-testid="button-mobile-consultation"
              >
                Book Your Free Consultation
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
